import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Lock, RotateCcw, Trash2, Plus, Gavel, ChevronLeft, ChevronRight, Save, Loader2, Copy,
  Send, AlertTriangle, Clock, Coffee, Euro, Wallet, Calendar, MessageSquare, Eye
} from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const HOUR_FIELDS = [
  { key: 'heures_normales', label: 'Heures normales' },
  { key: 'heures_sup_25', label: 'HS 25 %' },
  { key: 'heures_sup_50', label: 'HS 50 %' },
  { key: 'heures_nuit', label: 'Heures de nuit' },
  { key: 'heures_dimanche', label: 'Heures dimanche / férié' },
];

const ELEMENT_TYPES = [
  { value: 'prime', label: 'Prime', defaultCode: 'PRIME', defaultUnite: 'EUR' },
  { value: 'indemnite', label: 'Indemnité', defaultCode: 'INDEMNITE', defaultUnite: 'EUR' },
  { value: 'deduction', label: 'Retenue', defaultCode: 'RETENUE', defaultUnite: 'EUR' },
  { value: 'heures', label: 'Heures', defaultCode: 'HSUP25', defaultUnite: 'HEURES' },
];
const HEURES_CODES = [
  { code: 'HSUP25', label: 'Heures sup. 25 %' },
  { code: 'HSUP50', label: 'Heures sup. 50 %' },
  { code: 'HNUIT', label: 'Heures de nuit' },
  { code: 'HDIM', label: 'Heures dimanche / férié' },
];
const UNITES = ['EUR', 'HEURES', 'JOURS', 'NOMBRE'];

const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
let tempCounter = 0;
const tempId = () => `tmp-${Date.now()}-${tempCounter++}`;

/**
 * Fiche paie complète d'un salarié pour une période : heures, absences, paniers, éléments variables,
 * acomptes, jours fériés travaillés, saisies, commentaire, aperçu de l'envoi paie.
 * Un seul bouton Enregistrer (PUT /paie/recap/{periode}/{employee_id}/complet).
 */
const EmployeePayrollSheet = ({
  open, onOpenChange, employeeId, periode, isLocked = false, onSaved, onNavigate, hasPrev = false, hasNext = false,
  primeTypes = [], onOpenSaisies, canEdit = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fiche, setFiche] = useState(null);
  const [dirty, setDirty] = useState(false);

  const [hours, setHours] = useState({});
  const [paniers, setPaniers] = useState({ nb_jours: '', montant_unitaire: '', reset: false });
  const [elements, setElements] = useState([]);
  const [acomptes, setAcomptes] = useState([]);
  const [joursFeries, setJoursFeries] = useState([]);
  const [commentaire, setCommentaire] = useState('');

  const [apercu, setApercu] = useState(null);
  const [apercuLoading, setApercuLoading] = useState(false);

  const recap = fiche?.recap;
  const isValidated = recap?.statut === 'valide';
  const locked = isLocked || isValidated || !!fiche?.is_locked;
  const readOnly = locked || !canEdit;

  const loadFiche = useCallback(async () => {
    if (!employeeId || !periode) return;
    setLoading(true);
    setApercu(null);
    try {
      const res = await axios.get(`${API_URL}/paie/recap/${periode}/${employeeId}/fiche`);
      const data = res.data;
      setFiche(data);
      const r = data.recap || {};
      const initHours = {};
      HOUR_FIELDS.forEach(({ key }) => {
        initHours[key] = { value: String(r[key] ?? 0), manual: Boolean(r[`${key}_manuel`]), reset: false };
      });
      setHours(initHours);
      setPaniers({
        nb_jours: r.paniers_repas ? String(r.paniers_repas.nb_jours ?? '') : '',
        montant_unitaire: r.paniers_repas ? String(r.paniers_repas.montant_unitaire ?? '') : '',
        reset: false,
      });
      setElements((data.elements || []).map((e) => ({
        _key: e.id, id: e.id, type_element: e.type_element || 'prime', code_rubrique: e.code_rubrique || '',
        libelle: e.libelle || '', quantite: String(e.quantite ?? 1), montant: String(e.montant ?? 0),
        unite: e.unite || (e.type_element === 'heures' ? 'HEURES' : 'EUR'), code: e.code || '', commentaire: e.commentaire || '',
      })));
      setAcomptes((data.acomptes || []).map((a) => ({ _key: a.id, id: a.id, montant: String(a.montant ?? ''), date_versement: a.date_versement || '', motif: a.motif || '' })));
      setJoursFeries((data.jours_feries_travailles || []).map((j) => ({ _key: j.id, id: j.id, date: j.date, heures_travaillees: String(j.heures_travaillees ?? ''), taux_majoration: String(j.taux_majoration ?? '') })));
      setCommentaire(r.commentaire || '');
      setDirty(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger la fiche paie');
      setFiche(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId, periode]);

  useEffect(() => { if (open) loadFiche(); }, [open, loadFiche]);

  const mark = (setter) => (...args) => { setter(...args); setDirty(true); };
  const setHoursD = mark(setHours);
  const setPaniersD = mark(setPaniers);
  const setElementsD = mark(setElements);
  const setAcomptesD = mark(setAcomptes);
  const setJoursFeriesD = mark(setJoursFeries);
  const setCommentaireD = mark(setCommentaire);

  const guard = () => {
    if (isValidated) { toast.error('Paie validée : dévalidez d\'abord pour modifier'); return false; }
    if (locked) { toast.error(fiche?.lock_reason || 'Cette période est verrouillée'); return false; }
    if (!canEdit) { toast.error('Vous n\'avez pas la permission de modifier la paie'); return false; }
    return true;
  };

  // ---- Heures ----
  const setHour = (key, value) => setHoursD((prev) => ({ ...prev, [key]: { value, manual: true, reset: false } }));
  const resetHour = (key) => setHoursD((prev) => ({ ...prev, [key]: { value: String(recap?.[`${key}_auto`] ?? 0), manual: false, reset: true } }));

  // ---- Éléments ----
  const addElement = (type = 'prime') => {
    const t = ELEMENT_TYPES.find((x) => x.value === type) || ELEMENT_TYPES[0];
    setElementsD((prev) => [...prev, { _key: tempId(), id: null, type_element: t.value, code_rubrique: t.defaultCode, libelle: '', quantite: '1', montant: '', unite: t.defaultUnite, code: '', commentaire: '' }]);
  };
  const updateElement = (key, patch) => setElementsD((prev) => prev.map((e) => (e._key === key ? { ...e, ...patch } : e)));
  const removeElement = (key) => setElementsD((prev) => prev.filter((e) => e._key !== key));
  const onRubriqueChange = (el, code) => {
    const pt = primeTypes.find((p) => p.code === code);
    const hc = HEURES_CODES.find((h) => h.code === code);
    const patch = { code_rubrique: code };
    if (!el.libelle) patch.libelle = pt?.label || hc?.label || (code === 'PRIME' ? 'Prime' : code === 'INDEMNITE' ? 'Indemnité' : code === 'RETENUE' ? 'Retenue' : '');
    updateElement(el._key, patch);
  };
  const onTypeChange = (el, type) => {
    const t = ELEMENT_TYPES.find((x) => x.value === type) || ELEMENT_TYPES[0];
    updateElement(el._key, { type_element: t.value, code_rubrique: t.defaultCode, unite: t.defaultUnite });
  };

  const reprendreMoisPrecedent = async () => {
    if (!guard()) return;
    try {
      const res = await axios.get(`${API_URL}/paie/recap/${periode}/${employeeId}/precedent`);
      const prev = res.data.elements || [];
      if (prev.length === 0) { toast.info(`Aucun élément récurrent trouvé sur ${res.data.periode_precedente}`); return; }
      const existingKeys = new Set(elements.map((e) => `${e.code_rubrique}|${e.libelle}`));
      const toAdd = prev.filter((e) => !existingKeys.has(`${e.code_rubrique}|${e.libelle}`));
      if (toAdd.length === 0) { toast.info('Ces éléments sont déjà présents'); return; }
      setElementsD((cur) => [...cur, ...toAdd.map((e) => ({
        _key: tempId(), id: null, type_element: e.type_element || 'prime', code_rubrique: e.code_rubrique || 'PRIME',
        libelle: e.libelle || '', quantite: String(e.quantite ?? 1), montant: String(e.montant ?? 0),
        unite: e.unite || 'EUR', code: e.code || '', commentaire: e.commentaire || '',
      }))]);
      toast.success(`${toAdd.length} élément(s) repris de ${res.data.periode_precedente}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la reprise');
    }
  };

  // ---- Acomptes ----
  const addAcompte = () => setAcomptesD((prev) => [...prev, { _key: tempId(), id: null, montant: '', date_versement: new Date().toISOString().slice(0, 10), motif: '' }]);
  const updateAcompte = (key, patch) => setAcomptesD((prev) => prev.map((a) => (a._key === key ? { ...a, ...patch } : a)));
  const removeAcompte = (key) => setAcomptesD((prev) => prev.filter((a) => a._key !== key));

  // ---- Jours fériés ----
  const feriesDispo = (fiche?.jours_feries_periode || []).filter((jf) => !joursFeries.some((j) => j.date === jf.date));
  const addJourFerie = (date) => {
    const taux = recap?.taux_majoration_jour_ferie ?? '';
    setJoursFeriesD((prev) => [...prev, { _key: tempId(), id: null, date, heures_travaillees: '7', taux_majoration: String(taux) }]);
  };
  const updateJourFerie = (key, patch) => setJoursFeriesD((prev) => prev.map((j) => (j._key === key ? { ...j, ...patch } : j)));
  const removeJourFerie = (key) => setJoursFeriesD((prev) => prev.filter((j) => j._key !== key));

  // ---- Aperçu envoi paie ----
  const loadApercu = async () => {
    setApercuLoading(true);
    try {
      const res = await axios.get(`${API_URL}/paie/recap/${periode}/${employeeId}/apercu-envoi`);
      setApercu(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aperçu indisponible');
    } finally {
      setApercuLoading(false);
    }
  };

  // ---- Enregistrement groupé ----
  const buildPayload = () => {
    const heuresPayload = {};
    HOUR_FIELDS.forEach(({ key }) => {
      const h = hours[key];
      if (!h) return;
      if (h.reset) {
        if (recap?.[`${key}_manuel`]) heuresPayload[key] = null;
      } else if (h.manual && toNumber(h.value) !== toNumber(recap?.[key])) {
        heuresPayload[key] = toNumber(h.value);
      }
    });
    let paniersPayload = null;
    if (recap?.paniers_repas) {
      if (paniers.reset) paniersPayload = { nb_jours: null, montant_unitaire: null };
      else {
        paniersPayload = {};
        if (paniers.nb_jours !== '' && toNumber(paniers.nb_jours) !== toNumber(recap.paniers_repas.nb_jours)) paniersPayload.nb_jours = toNumber(paniers.nb_jours);
        if (paniers.montant_unitaire !== '' && toNumber(paniers.montant_unitaire) !== toNumber(recap.paniers_repas.montant_unitaire)) paniersPayload.montant_unitaire = toNumber(paniers.montant_unitaire);
        if (Object.keys(paniersPayload).length === 0) paniersPayload = null;
      }
    }
    return {
      heures: Object.keys(heuresPayload).length ? heuresPayload : null,
      paniers: paniersPayload,
      elements: elements
        .filter((e) => (e.libelle || e.code_rubrique) && (toNumber(e.montant) !== 0 || toNumber(e.quantite) !== 0))
        .map((e) => ({
          id: e.id, type_element: e.type_element, code_rubrique: e.code_rubrique, libelle: e.libelle || e.code_rubrique,
          quantite: toNumber(e.quantite), montant: toNumber(e.montant), unite: e.unite, code: e.code || null, commentaire: e.commentaire || null,
        })),
      acomptes: acomptes.filter((a) => toNumber(a.montant) > 0 && a.date_versement).map((a) => ({ id: a.id, montant: toNumber(a.montant), date_versement: a.date_versement, motif: a.motif || null })),
      jours_feries: joursFeries.filter((j) => toNumber(j.heures_travaillees) > 0).map((j) => ({ id: j.id, date: j.date, heures_travaillees: toNumber(j.heures_travaillees), taux_majoration: j.taux_majoration === '' ? null : toNumber(j.taux_majoration) })),
      commentaire: commentaire !== (recap?.commentaire || '') ? commentaire : null,
    };
  };

  const handleSave = async ({ thenNavigate = null } = {}) => {
    if (!guard()) return false;
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/paie/recap/${periode}/${employeeId}/complet`, buildPayload());
      toast.success('Fiche paie enregistrée');
      setDirty(false);
      if (onSaved) onSaved(res.data.recap);
      if (thenNavigate) onNavigate(thenNavigate);
      else await loadFiche();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const navigate = async (direction) => {
    if (dirty && !readOnly) {
      if (window.confirm('Des modifications ne sont pas enregistrées. Enregistrer avant de changer de salarié ?')) {
        const ok = await handleSave({ thenNavigate: direction });
        if (!ok) return;
        return;
      }
    }
    onNavigate(direction);
  };

  const handleOpenChange = (o) => {
    if (!o && dirty && !readOnly && !window.confirm('Des modifications ne sont pas enregistrées. Fermer quand même ?')) return;
    onOpenChange(o);
  };

  const totalElements = (type) => elements.filter((e) => (type === 'deduction' ? ['deduction', 'retenue'].includes(e.type_element) : e.type_element === type)).reduce((s, e) => s + toNumber(e.montant), 0);
  const totalAcomptes = acomptes.reduce((s, a) => s + toNumber(a.montant), 0);
  const rubriqueOptions = (el) => {
    if (el.type_element === 'heures') return HEURES_CODES.map((h) => ({ code: h.code, label: h.label }));
    const generic = el.type_element === 'indemnite' ? [{ code: 'INDEMNITE', label: 'Indemnité (générique)' }]
      : el.type_element === 'deduction' ? [{ code: 'RETENUE', label: 'Retenue (générique)' }]
        : [{ code: 'PRIME', label: 'Prime (générique)' }];
    const custom = primeTypes.map((p) => ({ code: p.code, label: p.label }));
    const all = [...generic, ...custom];
    if (el.code_rubrique && !all.some((o) => o.code === el.code_rubrique)) all.push({ code: el.code_rubrique, label: el.code_rubrique });
    return all;
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto p-0">
        <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b px-6 py-4">
          <SheetHeader className="space-y-1">
            <div className="flex items-start justify-between gap-3 pr-8">
              <div>
                <SheetTitle className="text-lg">
                  {recap ? `${recap.employee_name}` : 'Fiche paie'}
                  {recap?.matricule_paie && <span className="ml-2 font-mono text-sm text-slate-500">mat. paie {recap.matricule_paie}</span>}
                </SheetTitle>
                <SheetDescription className="flex flex-wrap items-center gap-2">
                  <span>Période {periode}</span>
                  {recap?.poste && <span>· {recap.poste}</span>}
                  {recap?.type_contrat && <Badge variant="outline" className="text-[10px]">{recap.type_contrat}</Badge>}
                  {recap?.salaire_base ? <span>· Base {formatCurrency(recap.salaire_base)}</span> : null}
                  {recap?.taux_horaire ? <span>· {formatCurrency(recap.taux_horaire)}/h</span> : null}
                  {isValidated && <Badge className="bg-emerald-100 text-emerald-800 text-[10px]"><Lock className="w-3 h-3 mr-1" />Validée</Badge>}
                  {locked && !isValidated && <Badge className="bg-red-100 text-red-800 text-[10px]"><Lock className="w-3 h-3 mr-1" />Verrouillée</Badge>}
                  {!recap?.matricule_paie && recap && <Badge className="bg-amber-100 text-amber-800 text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" />Sans matricule paie</Badge>}
                </SheetDescription>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="outline" size="icon" onClick={() => navigate(-1)} disabled={!hasPrev || saving} title="Salarié précédent"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="icon" onClick={() => navigate(1)} disabled={!hasNext || saving} title="Salarié suivant"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </SheetHeader>
        </div>

        {loading || !recap ? (
          <div className="flex items-center justify-center h-64 text-slate-500">
            {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Aucune donnée'}
          </div>
        ) : (
          <div className="px-6 py-4 space-y-6 pb-28">
            {readOnly && (
              <div className="p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {isValidated ? 'Paie validée pour ce salarié : consultation seule (dévalidez pour modifier).' : (fiche?.lock_reason || (!canEdit ? 'Consultation seule.' : 'Période verrouillée : consultation seule.'))}
              </div>
            )}

            {/* Heures */}
            <section>
              <h3 className="font-semibold flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-[#0F5156]" /> Heures</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {HOUR_FIELDS.map(({ key, label }) => {
                  const h = hours[key] || { value: '0', manual: false };
                  return (
                    <div key={key} className="p-3 rounded-md border bg-slate-50/50 dark:bg-slate-800/40">
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs">{label}</Label>
                        <Badge variant="outline" className={`text-[10px] ${h.manual ? 'border-amber-400 text-amber-700' : 'text-slate-500'}`}>{h.manual ? 'Manuel' : 'Auto'}</Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Input type="number" step="0.25" min="0" value={h.value} disabled={readOnly} onChange={(e) => setHour(key, e.target.value)} className="h-8" />
                        {h.manual && !readOnly && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title={`Revenir au calcul (${recap[`${key}_auto`] ?? 0})`} onClick={() => resetHour(key)}><RotateCcw className="w-3.5 h-3.5" /></Button>
                        )}
                      </div>
                      {h.manual && <p className="text-[10px] text-slate-500 mt-1">Calculé : {recap[`${key}_auto`] ?? 0}</p>}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Absences */}
            <section>
              <h3 className="font-semibold flex items-center gap-2 mb-2"><Calendar className="w-4 h-4 text-[#0F5156]" /> Absences de la période <span className="text-xs font-normal text-slate-500">(module Congés, lecture seule)</span></h3>
              {(recap.absences || []).length === 0 ? (
                <p className="text-sm text-slate-500">Aucune absence validée</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {recap.absences.map((a, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{a.type_label || a.type} · {a.nb_jours} j{a.date_debut ? ` · du ${new Date(a.date_debut + 'T00:00:00').toLocaleDateString('fr-FR')}` : ''}</Badge>
                  ))}
                </div>
              )}
            </section>

            {/* Paniers */}
            {recap.paniers_repas && (
              <section>
                <h3 className="font-semibold flex items-center gap-2 mb-3"><Coffee className="w-4 h-4 text-[#0F5156]" /> Paniers repas</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <div>
                    <Label className="text-xs">Nombre de jours <span className="text-slate-400">(calculé : {recap.paniers_repas.nb_jours_ouvres ?? '-'} ouvrés − {recap.paniers_repas.nb_jours_absence ?? 0} abs.)</span></Label>
                    <Input type="number" min="0" value={paniers.nb_jours} disabled={readOnly} onChange={(e) => setPaniersD((p) => ({ ...p, nb_jours: e.target.value, reset: false }))} className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs">Montant unitaire</Label>
                    <Input type="number" step="0.01" min="0" value={paniers.montant_unitaire} disabled={readOnly} onChange={(e) => setPaniersD((p) => ({ ...p, montant_unitaire: e.target.value, reset: false }))} className="h-8" />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">Total : {formatCurrency(toNumber(paniers.nb_jours) * toNumber(paniers.montant_unitaire))}</span>
                    {!readOnly && <Button variant="ghost" size="sm" onClick={() => setPaniersD((p) => ({ ...p, reset: true, nb_jours: String(recap.paniers_repas.nb_jours_ouvres - (recap.paniers_repas.nb_jours_absence || 0)) }))}><RotateCcw className="w-3.5 h-3.5 mr-1" />Auto</Button>}
                  </div>
                </div>
              </section>
            )}

            {/* Éléments variables */}
            <section>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="font-semibold flex items-center gap-2"><Euro className="w-4 h-4 text-[#0F5156]" /> Éléments variables</h3>
                {!readOnly && (
                  <div className="flex gap-1 flex-wrap">
                    <Button variant="outline" size="sm" onClick={() => addElement('prime')}><Plus className="w-3.5 h-3.5 mr-1" />Prime</Button>
                    <Button variant="outline" size="sm" onClick={() => addElement('indemnite')}><Plus className="w-3.5 h-3.5 mr-1" />Indemnité</Button>
                    <Button variant="outline" size="sm" onClick={() => addElement('deduction')}><Plus className="w-3.5 h-3.5 mr-1" />Retenue</Button>
                    <Button variant="outline" size="sm" onClick={() => addElement('heures')}><Plus className="w-3.5 h-3.5 mr-1" />Heures</Button>
                    <Button variant="ghost" size="sm" onClick={reprendreMoisPrecedent} title="Reprendre les primes et indemnités du mois précédent"><Copy className="w-3.5 h-3.5 mr-1" />Mois précédent</Button>
                  </div>
                )}
              </div>
              {elements.length === 0 ? (
                <p className="text-sm text-slate-500">Aucun élément variable</p>
              ) : (
                <div className="space-y-2">
                  <div className="hidden lg:grid grid-cols-12 gap-2 text-[11px] text-slate-500 px-1">
                    <span className="col-span-2">Type</span><span className="col-span-3">Rubrique</span><span className="col-span-3">Libellé</span><span className="col-span-1">Qté</span><span className="col-span-1">Unité</span><span className="col-span-1 text-right">Montant</span><span className="col-span-1"></span>
                  </div>
                  {elements.map((el) => (
                    <div key={el._key} className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-center p-2 rounded-md border bg-white dark:bg-slate-900">
                      <div className="lg:col-span-2">
                        <Select value={el.type_element} onValueChange={(v) => onTypeChange(el, v)} disabled={readOnly}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{ELEMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="lg:col-span-3">
                        <Select value={el.code_rubrique || ''} onValueChange={(v) => onRubriqueChange(el, v)} disabled={readOnly}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Rubrique" /></SelectTrigger>
                          <SelectContent className="max-h-60">{rubriqueOptions(el).map((o) => <SelectItem key={o.code} value={o.code}>{o.label} <span className="text-slate-400 font-mono text-[10px]">({o.code})</span></SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="lg:col-span-3"><Input className="h-8 text-xs" placeholder="Libellé" value={el.libelle} disabled={readOnly} onChange={(e) => updateElement(el._key, { libelle: e.target.value })} /></div>
                      <div className="lg:col-span-1"><Input className="h-8 text-xs" type="number" step="0.25" value={el.quantite} disabled={readOnly} onChange={(e) => updateElement(el._key, { quantite: e.target.value })} /></div>
                      <div className="lg:col-span-1">
                        <Select value={el.unite} onValueChange={(v) => updateElement(el._key, { unite: v })} disabled={readOnly}>
                          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>{UNITES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="lg:col-span-1"><Input className="h-8 text-xs text-right" type="number" step="0.01" placeholder="0,00" value={el.montant} disabled={readOnly} onChange={(e) => updateElement(el._key, { montant: e.target.value })} /></div>
                      <div className="lg:col-span-1 flex justify-end">{!readOnly && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => removeElement(el._key)}><Trash2 className="w-3.5 h-3.5" /></Button>}</div>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-4 text-xs text-slate-600 dark:text-slate-300 px-1">
                    <span>Primes : <strong>{formatCurrency(totalElements('prime'))}</strong></span>
                    <span>Indemnités : <strong>{formatCurrency(totalElements('indemnite'))}</strong></span>
                    <span>Retenues : <strong className="text-red-600">{formatCurrency(totalElements('deduction'))}</strong></span>
                  </div>
                </div>
              )}
            </section>

            {/* Acomptes */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2"><Wallet className="w-4 h-4 text-[#0F5156]" /> Acomptes <span className="text-xs font-normal text-slate-500">{totalAcomptes > 0 ? `· ${formatCurrency(totalAcomptes)}` : ''}</span></h3>
                {!readOnly && <Button variant="outline" size="sm" onClick={addAcompte}><Plus className="w-3.5 h-3.5 mr-1" />Acompte</Button>}
              </div>
              {acomptes.length === 0 ? <p className="text-sm text-slate-500">Aucun acompte</p> : (
                <div className="space-y-2">
                  {acomptes.map((a) => (
                    <div key={a._key} className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-center p-2 rounded-md border">
                      <div className="lg:col-span-3"><Input className="h-8 text-xs" type="date" value={a.date_versement} disabled={readOnly} onChange={(e) => updateAcompte(a._key, { date_versement: e.target.value })} /></div>
                      <div className="lg:col-span-3"><Input className="h-8 text-xs text-right" type="number" step="0.01" placeholder="Montant" value={a.montant} disabled={readOnly} onChange={(e) => updateAcompte(a._key, { montant: e.target.value })} /></div>
                      <div className="lg:col-span-5"><Input className="h-8 text-xs" placeholder="Motif" value={a.motif} disabled={readOnly} onChange={(e) => updateAcompte(a._key, { motif: e.target.value })} /></div>
                      <div className="lg:col-span-1 flex justify-end">{!readOnly && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => removeAcompte(a._key)}><Trash2 className="w-3.5 h-3.5" /></Button>}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Jours fériés travaillés */}
            <section>
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="font-semibold flex items-center gap-2"><Calendar className="w-4 h-4 text-[#0F5156]" /> Jours fériés travaillés</h3>
                {!readOnly && feriesDispo.length > 0 && (
                  <Select value="" onValueChange={addJourFerie}>
                    <SelectTrigger className="h-8 w-56 text-xs"><SelectValue placeholder="+ Déclarer un jour férié" /></SelectTrigger>
                    <SelectContent>{feriesDispo.map((jf) => <SelectItem key={jf.date} value={jf.date}>{new Date(jf.date + 'T00:00:00').toLocaleDateString('fr-FR')} · {jf.nom}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              {(fiche?.jours_feries_periode || []).length === 0 ? <p className="text-sm text-slate-500">Aucun jour férié sur cette période</p>
                : joursFeries.length === 0 ? <p className="text-sm text-slate-500">Aucun jour férié travaillé déclaré</p> : (
                  <div className="space-y-2">
                    {joursFeries.map((j) => {
                      const nom = (fiche?.jours_feries_periode || []).find((f) => f.date === j.date)?.nom || '';
                      return (
                        <div key={j._key} className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-center p-2 rounded-md border">
                          <div className="lg:col-span-4 text-sm">{new Date(j.date + 'T00:00:00').toLocaleDateString('fr-FR')} <span className="text-xs text-slate-500">{nom}</span></div>
                          <div className="lg:col-span-3"><Label className="text-[10px]">Heures</Label><Input className="h-8 text-xs" type="number" step="0.25" value={j.heures_travaillees} disabled={readOnly} onChange={(e) => updateJourFerie(j._key, { heures_travaillees: e.target.value })} /></div>
                          <div className="lg:col-span-3"><Label className="text-[10px]">Majoration %</Label><Input className="h-8 text-xs" type="number" step="1" value={j.taux_majoration} disabled={readOnly} onChange={(e) => updateJourFerie(j._key, { taux_majoration: e.target.value })} /></div>
                          <div className="lg:col-span-2 flex justify-end">{!readOnly && <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => removeJourFerie(j._key)}><Trash2 className="w-3.5 h-3.5" /></Button>}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </section>

            {/* Saisies */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold flex items-center gap-2"><Gavel className="w-4 h-4 text-[#0F5156]" /> Saisies sur salaire <span className="text-xs font-normal text-slate-500">{recap.total_saisies ? `· ${formatCurrency(recap.total_saisies)} ce mois` : ''}</span></h3>
                {onOpenSaisies && <Button variant="outline" size="sm" onClick={() => onOpenSaisies(employeeId)}>Gérer les saisies</Button>}
              </div>
              {(recap.saisies || []).length === 0 ? <p className="text-sm text-slate-500">Aucune saisie en cours</p> : (
                <ul className="text-sm space-y-1">{recap.saisies.map((s, i) => <li key={i} className="flex justify-between"><span>{s.creancier || s.libelle || 'Saisie'}</span><span className="font-mono">{formatCurrency(s.retenue_mois ?? s.montant ?? 0)}</span></li>)}</ul>
              )}
            </section>

            {/* Commentaire */}
            <section>
              <h3 className="font-semibold flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-[#0F5156]" /> Commentaire pour le gestionnaire de paie</h3>
              <Textarea rows={2} value={commentaire} disabled={readOnly} onChange={(e) => setCommentaireD(e.target.value)} placeholder="Informations à transmettre avec la paie..." />
            </section>

            {/* Aperçu envoi */}
            <section className="border-t pt-4">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="font-semibold flex items-center gap-2"><Send className="w-4 h-4 text-[#0F5156]" /> Aperçu de l'envoi vers le logiciel de paie</h3>
                <Button variant="outline" size="sm" onClick={loadApercu} disabled={apercuLoading}>{apercuLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Eye className="w-3.5 h-3.5 mr-1" />}{apercu ? 'Actualiser' : 'Afficher'}</Button>
              </div>
              {dirty && apercu && <p className="text-xs text-amber-700 mb-2">L'aperçu reflète les données enregistrées : enregistrez pour le mettre à jour.</p>}
              {apercu && (
                <div className="space-y-2 text-sm">
                  {!apercu.provider_configured && <p className="text-xs text-slate-500">Aucun logiciel de paie configuré : l'aperçu montre les éléments avec les codes mappés dans Administration › Codes paie.</p>}
                  {apercu.sans_matricule && <p className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Ce salarié n'a pas de matricule paie : il ne sera pas inclus dans l'envoi.</p>}
                  {apercu.elements.length === 0 ? <p className="text-slate-500">Aucun élément à transmettre</p> : (
                    <table className="w-full text-xs">
                      <thead><tr className="text-slate-500 text-left"><th className="py-1">Code {apercu.provider ? apercu.provider : ''}</th><th>Libellé</th><th className="text-right">Valeur</th><th>Unité</th></tr></thead>
                      <tbody>{apercu.elements.map((e, i) => <tr key={i} className="border-t"><td className="py-1 font-mono">{e.code}</td><td>{e.libelle}</td><td className="text-right font-mono">{e.valeur}</td><td>{e.unite}</td></tr>)}</tbody>
                    </table>
                  )}
                  {apercu.non_mappes.length > 0 && (
                    <div className="p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-xs text-amber-800 dark:text-amber-200">
                      <p className="font-medium flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{apercu.non_mappes.length} élément(s) sans code paie configuré (non transmis) :</p>
                      <ul className="mt-1 space-y-0.5">{apercu.non_mappes.map((n, i) => <li key={i}>• {n.libelle} <span className="font-mono">({n.nexus_code})</span> — {n.valeur} {n.unite}</li>)}</ul>
                      <p className="mt-1">Configurez le mapping dans Administration › Préparation paie › Codes paie.</p>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}

        {/* Barre d'actions fixe */}
        {recap && (
          <div className="sticky bottom-0 bg-white dark:bg-slate-900 border-t px-6 py-3 flex items-center justify-between gap-2">
            <div className="text-xs text-slate-500">{dirty && !readOnly ? 'Modifications non enregistrées' : ''}</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => handleOpenChange(false)}>Fermer</Button>
              {!readOnly && (
                <>
                  <Button className="bg-[#0F5156] hover:bg-[#0c4145] text-white gap-2" onClick={() => handleSave()} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
                  </Button>
                  {hasNext && (
                    <Button variant="secondary" className="gap-1" onClick={() => handleSave({ thenNavigate: 1 })} disabled={saving} title="Enregistrer et passer au salarié suivant">
                      Enregistrer et suivant <ChevronRight className="w-4 h-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default EmployeePayrollSheet;
